<template>
    <section
        class="cards-list-banner"
        :class="{
            [`size-${display.size}`]: true,
        }"
    >
        <div class="inner">
            <h2 v-if="title" class="h2">{{ title }}</h2>
            <div
                v-for="card in cards"
                :key="card.id"
                class="card"
                :class="{
                    [$cms.computedBackgroundClass(display, layout?.name)]: true,
                }"
            >
                <div class="card-grid" :class="{'card-grid-center': display.alignment === 'center', 'card-grid-prices': card.products, 'card-grid-right': display.alignment === 'right' }">
                    <div
                        class="content"
                        :class="{
                            [`alignment-${display.alignment}`]: true,
                            'no-text': !card.text,
                        }"
                    >
                        <p v-if="card.byline" class="byline h6">{{ card.byline }}</p>
                        <p v-else-if="card.products" class="byline h6">{{ $cms.productArticle(card.products).attributes.short_description }}</p>
                        <h3
                            v-if="card.title"
                            class="title"
                            :class="display.size === 'full-width' ? 'h2' : 'h3'"
                        >
                            {{ card.title }}
                        </h3>
                        <h3
                            v-else-if="card.products"
                            class="title"
                            :class="display.size === 'full-width' ? 'h2' : 'h3'"
                        >
                            {{ $cms.productArticle(card.products).attributes.product_name }}
                        </h3>
                        <DSCFText
                            v-if="card.text"
                            class="text"
                            :content="card.text"
                        />
                        <DSPrice
                            v-if="card.products"
                            :products="card.products"
                            @add-product="addProduct"
                        />
                        <div v-if="card.links" class="buttons">
                            <NuxtLink
                                v-for="(link, key) in card.links.filter(link => link.title)"
                                :key="key"
                                v-bind="$cms.parseLink(link)"
                                :class="resolveLinkClass(key, link)"
                            >
                                <span>{{ link.title }}</span>
                            </NuxtLink>
                        </div>
                    </div>
                    <DSFrontifyAsset
                        v-if="card.image && card.image[0] && display.size !== 'small' && display.alignment !== 'center'"
                        :asset="card.image"
                        fill="cover"
                    />

                    <SLMap
                        v-if="hasStationLocator(card)"
                        :content-type="card.type"
                        :data="card.text?.content?.[1]?.data?.target?.data || []"
                        :display="card.data"
                    />
                </div>
            </div>
        </div>
    </section>
</template>
<script setup>
import { inject, useNuxtApp } from '#imports';
import SLMap from '../../StationLocator/Map.vue';
const { $cms } = useNuxtApp();
const emits = defineEmits('addProduct');
const layout = inject('layout');

defineProps({
    contentType: {
        type: String,
        required: false,
        default: 'cardList',
    },
    internal: {
        type: String,
        required: false,
        default: 'Card List',
    },
    display: {
        type: Object,
        required: false,
        default: () => ({
            component: 'list',
            background: 'primary',
            foreground: 'light',
        }),
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    cards: {
        type: Array,
        required: false,
        default: () => [],
    },
});

function addProduct(product) {
    emits('addProduct', product);
    $cms.addProduct(product);
}

function resolveLinkClass(key, link) {
    return `ds-btn ds-btn--large ds-btn--${key === 0 && !link.image ? 'primary' : 'secondary'}`;
}

function hasStationLocator(card){
    return card.text?.content?.some(c => c.nodeType === 'embedded-entry-block');
}
</script>
<script>
export default {
    name: 'CardListBanner',
};
</script>
<style scoped>
.cards-list-banner {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.cards-list-banner .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.cards-list-banner {
  padding-top: 4rem;
}
.cards-list-banner:not(:last-child) {
  padding-bottom: 4rem;
}
.cards-list-banner h2 {
  max-width: var(--page-max-width);
  margin-top: -2rem;
  margin-inline: auto;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  justify-self: end;
}
.cards-list-banner .card.bg-primary {
  background: var(--color-primary-500);
}
.cards-list-banner .card.bg-secondary {
  background: var(--color-secondary-500);
}
.cards-list-banner .card.bg-tertiary {
  background: var(--color-tertiary-500);
}
.cards-list-banner .card.fg-light .h1,
.cards-list-banner .card.fg-light .h2,
.cards-list-banner .card.fg-light .h3,
.cards-list-banner .card.fg-light .h4,
.cards-list-banner .card.fg-light .h5,
.cards-list-banner .card.fg-light .h6,
.cards-list-banner .card.fg-light .rich-text,
.cards-list-banner .card.fg-light .byline {
  color: var(--color-base-white);
}
.cards-list-banner .card.fg-light .ds-btn--primary {
  background: var(--color-secondary-500);
  color: var(--color-base-black);
}
.cards-list-banner .card.fg-light .ds-btn--primary:hover {
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.cards-list-banner .card.fg-light .ds-btn--secondary {
  background: none;
  color: var(--color-base-white);
  border-color: var(--color-base-white);
}
.cards-list-banner .card.fg-light .ds-btn--secondary:hover {
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.cards-list-banner .card .card-grid {
  display: grid;
}
.cards-list-banner .card .image,
.cards-list-banner .card .video {
  padding-top: 50%;
}
.cards-list-banner .card .content {
  align-self: center;
}
@media (min-width: 1200px) {
  .cards-list-banner .card .content {
    padding: 2rem 1.5rem;
  }
}
.cards-list-banner .card .alignment-center {
  text-align: center;
  padding-right: 2rem;
  grid-column-start: 1;
  grid-column-end: 3;
}
.cards-list-banner .card .alignment-center .buttons {
  margin: 1rem auto;
}
.cards-list-banner .card:not(:last-child) {
  margin-bottom: 1rem;
}
.cards-list-banner .card .title {
  word-wrap: break-word;
  hyphens: auto;
  max-width: calc(100dvw - 6rem);
}
.cards-list-banner .buttons {
  width: fit-content;
  margin-top: 1.5rem;
  margin-left: -1rem;
  margin-bottom: -1rem;
}
.cards-list-banner .buttons > * {
  margin-left: 1rem;
  margin-bottom: 1rem;
}
.cards-list-banner .byline {
  margin-bottom: 0;
}
.cards-list-banner .byline + .title {
  margin-top: 0;
}
.cards-list-banner.size-full-width .inner {
  max-width: 100%;
  padding-left: 0;
  padding-right: 0;
}
.cards-list-banner.size-full-width .card-grid {
  margin-left: auto;
  grid-template-columns: 2fr minmax(33.3333%, 1fr);
}
@media (min-width: 1200px) {
  .cards-list-banner.size-full-width .card-grid.card-grid-right {
    grid-template-columns: 1fr 1fr;
  }
  .cards-list-banner.size-full-width .card-grid.card-grid-right .image {
    grid-column-start: 1;
    grid-row-start: 1;
  }
  .cards-list-banner.size-full-width .card-grid.card-grid-right .content {
    grid-column-start: 2;
  }
}
@media (max-width: 991px) {
  .cards-list-banner.size-full-width .card-grid {
    grid-template-columns: 1fr;
  }
  .cards-list-banner.size-full-width .card-grid .image,
  .cards-list-banner.size-full-width .card-grid .video {
    order: -1;
  }
}
.cards-list-banner.size-full-width .card-grid-center {
  margin: 0 auto;
  max-width: var(--page-max-width);
  padding: 2rem 6rem;
}
@media (max-width: 1199px) {
  .cards-list-banner.size-full-width .card-grid-center {
    padding: 2rem 1.5rem;
  }
}
@media (min-width: 992px) {
  .cards-list-banner.size-full-width .card-grid-prices .content {
    display: grid;
    grid-template-rows: auto auto;
    grid-template-columns: auto auto;
  }
  .cards-list-banner.size-full-width .card-grid-prices .content .byline,
  .cards-list-banner.size-full-width .card-grid-prices .content .title,
  .cards-list-banner.size-full-width .card-grid-prices .content .text {
    grid-column-start: 1;
    grid-column-end: span 2;
  }
  .cards-list-banner.size-full-width .card-grid-prices .content .buttons {
    grid-column-start: 1;
    grid-row-start: 4;
    align-self: end;
  }
  .cards-list-banner.size-full-width .card-grid-prices .content .prices {
    grid-column-start: 2;
    grid-row-start: 4;
  }
}
.cards-list-banner.size-normal .card-grid {
  display: grid;
  grid-template-columns: 2fr minmax(467px, 1fr);
}
@media (min-width: 1200px) {
  .cards-list-banner.size-normal .card-grid.card-grid-right {
    grid-template-columns: 1fr 1fr;
  }
  .cards-list-banner.size-normal .card-grid.card-grid-right .image {
    grid-column-start: 1;
    grid-row-start: 1;
    border-radius: 1rem 0 0 1rem;
  }
  .cards-list-banner.size-normal .card-grid.card-grid-right .content {
    grid-column-start: 2;
  }
}
@media (max-width: 991px) {
  .cards-list-banner.size-normal .card-grid {
    grid-template-columns: 1fr;
  }
  .cards-list-banner.size-normal .card-grid .image,
  .cards-list-banner.size-normal .card-grid .video {
    order: -1;
  }
}
@media (min-width: 992px) {
  .cards-list-banner.size-small .content {
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-gap: 0 2rem;
  }
  .cards-list-banner.size-small .content *:not(.buttons) {
    grid-column-start: 1;
  }
  .cards-list-banner.size-small .content .buttons {
    grid-column-start: 2;
    grid-row-start: 1;
    grid-row-end: span 2;
    padding: 0;
    align-self: center;
  }
}
.cards-list-banner .bg-custom {
  background-color: v-bind("display.background");
}
.cards-list-banner .bg-custom .buttons .ds-link {
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.cards-list-banner .bg-custom .buttons .ds-link:hover {
  background: var(--color-base-black);
  color: var(--color-base-white);
}
.cards-list-banner .no-text {
  grid-template-rows: 1fr;
}
.cards-list-banner .no-text h3 {
  margin: 0;
  align-self: center;
}
.cards-list-banner .no-text .buttons {
  margin-bottom: 0;
  padding: 0;
}
</style>

